import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import OTPInput from '../../components/OTPInput';

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get('email') || '';
  const flow = searchParams.get('flow') || '';
  
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!email) {
      navigate('/auth/register');
    }
  }, [email, navigate]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleOtpChange = (value: string) => {
    setOtp(value);
    setError('');
    
    if (value.length === 6) {
      handleVerifyOTP(value);
    }
  };

  const handleVerifyOTP = async (code?: string) => {
    const codeToVerify = code || otp;
    
    if (!codeToVerify || codeToVerify.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (flow === 'job_seeker') {
        await apiService.verifyJobSeekerEmailOTP(email, codeToVerify);
        setSuccess('Email verified successfully! Check your email for login credentials.');
        
        setTimeout(() => {
          navigate('/auth/login?message=Check your email for temporary password');
        }, 3000);
      } else if (flow === 'company_user') {
        await apiService.verifyCompanyUserEmailOTP(email, codeToVerify);
        setSuccess('Email verified successfully! Check your email for login credentials.');
        
        setTimeout(() => {
          navigate('/auth/login?message=Check your email for temporary password');
        }, 3000);
      } else {
        await apiService.verifyEmailOTP(email, codeToVerify);
        setSuccess('Email verified successfully! Redirecting to dashboard...');
        
        await refreshUser();
        
        setTimeout(() => {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          if (userData.company_id || userData.role) {
            navigate('/company/dashboard');
          } else {
            navigate('/candidate/dashboard');
          }
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await apiService.resendVerificationOTP(email);
      setSuccess('Verification code sent successfully!');
      setResendCooldown(60);
      setOtp('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
            <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            We've sent a 6-digit verification code to
          </p>
          <p className="text-sm font-medium text-indigo-600">
            {email}
          </p>
          {(flow === 'job_seeker' || flow === 'company_user') && (
            <p className="mt-2 text-xs text-gray-500">
              After verification, we'll send your login credentials to your email.
            </p>
          )}
        </div>

        <div className="bg-white shadow-xl rounded-lg p-8">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
              {success}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                Enter Verification Code
              </label>
              <OTPInput
                length={6}
                value={otp}
                onChange={handleOtpChange}
                disabled={loading}
                autoFocus={true}
              />
              <p className="mt-3 text-xs text-gray-500 text-center">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            {loading && (
              <div className="flex justify-center items-center py-4">
                <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="ml-2 text-sm text-gray-600">Verifying...</span>
              </div>
            )}

            <button
              onClick={() => handleVerifyOTP()}
              disabled={loading || otp.length !== 6}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out"
            >
              Verify Email
            </button>
          </div>

          <div className="mt-6 text-center space-y-4">
            <p className="text-sm text-gray-600">
              Didn't receive the code?
            </p>
            
            <button
              onClick={handleResendOTP}
              disabled={loading || resendCooldown > 0}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:text-gray-400 disabled:cursor-not-allowed transition duration-150 ease-in-out"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </div>
              ) : resendCooldown > 0 ? (
                `Resend code in ${formatCountdown(resendCooldown)}`
              ) : (
                'Resend verification code'
              )}
            </button>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500">
                The verification code will expire in 10 minutes for security reasons.
              </p>
            </div>

            <div className="text-center">
              <Link
                to="/auth/register"
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                ← Back to Sign Up
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage; 